import React, { useEffect, useState } from "react";
import { ROLE_LABEL, type Role, type SmScope, type AdminUserRow } from "@analytics/shared";
import { api, useAsync, useApp } from "../store.js";
import { OK, ACC, UNK, PageHead, cardPad, Chip } from "../lib/ui.js";
import { MultiSelect, Field, inputStyle, type Opt } from "./LeadDistribution.js";

const ROLES: Role[] = ["admin", "board_member", "sales_manager", "receiver"];
const roleColor: Record<Role, string> = { admin: "#e79a9a", board_member: "#b5abfc", sales_manager: "#6f93d6", receiver: UNK };

export function Users() {
  const { session } = useApp();
  // Only ADMIN manages users (roles, scopes, add/remove). Others — and anyone while impersonating a
  // sales manager — have no access (the nav hides it too; this is the direct-URL safety net).
  if (!session?.canEditRoles) {
    return (
      <div>
        <PageHead title="Users" sub="Quản lý người dùng" />
        <div style={{ ...cardPad, color: UNK, fontSize: "13px" }}>
          Bạn không có quyền quản lý người dùng. (Chỉ Admin.)
        </div>
      </div>
    );
  }
  return <ManageUsers />;
}

function ManageUsers() {
  const { session, reloadSession } = useApp();
  const usersQ = useAsync(() => api.adminUsers(), []);
  const canEditRoles = Boolean(session?.canEditRoles);
  const [editing, setEditing] = useState<AdminUserRow | null>(null);
  const [savingRole, setSavingRole] = useState<string | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [newRole, setNewRole] = useState<Role>("receiver");
  const [adding, setAdding] = useState(false);

  const changeRole = async (email: string, role: Role) => {
    setSavingRole(email);
    try {
      await api.setUserRole(email, role);
      usersQ.reload();
      if (email === session?.email) await reloadSession();
    } catch { alert("Không đổi được vai trò."); }
    finally { setSavingRole(null); }
  };

  const addUser = async () => {
    const email = newEmail.trim().toLowerCase();
    if (!/^[^@\s]+@rever\.vn$/.test(email)) { alert("Email phải là địa chỉ @rever.vn hợp lệ."); return; }
    setAdding(true);
    try { await api.addUser(email, newRole); setNewEmail(""); setNewRole("receiver"); usersQ.reload(); }
    catch { alert("Không thêm được người dùng."); }
    finally { setAdding(false); }
  };

  const removeUser = async (email: string) => {
    if (!confirm(`Gỡ quyền đăng nhập của ${email}?`)) return;
    try { await api.removeUser(email); usersQ.reload(); }
    catch { alert("Không gỡ được người dùng (người dùng ở code không gỡ được tại đây)."); }
  };

  const rows = usersQ.data?.rows ?? [];

  return (
    <div>
      <PageHead
        title="Users & roles"
        sub="Vai trò và phạm vi dữ liệu — lưu trong config DB (analytics_portal). Admin đổi vai trò; Admin & Board sửa phạm vi của Sales manager."
        right={<Chip color={ACC}>{rows.length} users</Chip>}
      />

      {canEditRoles && (
        <div style={{ ...cardPad, display: "flex", gap: "10px", alignItems: "flex-end", flexWrap: "wrap", marginBottom: "10px" }}>
          <Field label="Thêm người dùng (email @rever.vn)">
            <input
              type="email" placeholder="ten@rever.vn" value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") addUser(); }}
              style={{ ...inputStyle, minWidth: "240px" }}
            />
          </Field>
          <Field label="Vai trò">
            <select value={newRole} onChange={(e) => setNewRole(e.target.value as Role)} style={{ ...inputStyle, minWidth: "150px" }}>
              {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </Field>
          <button onClick={addUser} disabled={adding} className="btn" style={{ height: "32px", fontSize: "12px", background: ACC, color: "#161826", border: 0, borderRadius: "8px", padding: "0 14px", cursor: "pointer" }}>
            <i className="ph ph-user-plus" /> {adding ? "Đang thêm…" : "Thêm"}
          </button>
          <span style={{ fontSize: "11px", color: "#75798c", alignSelf: "center" }}>Thêm = cấp quyền đăng nhập + vai trò (lưu ở DB, không cần deploy).</span>
        </div>
      )}

      <div style={cardPad}>
        <table className="table" style={{ fontSize: "12px", width: "100%" }}>
          <thead><tr>
            <th style={{ paddingLeft: 0 }}>Email</th><th>Role</th><th>Scope</th><th></th>
          </tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.email}>
                <td style={{ paddingLeft: 0, color: "#e9e9ed" }}>
                  {u.email}
                  {u.email === session?.email && <span style={{ color: OK, fontSize: "10px", marginLeft: "6px" }}>you</span>}
                </td>
                <td>
                  {canEditRoles ? (
                    <select
                      value={u.role}
                      disabled={savingRole === u.email}
                      onChange={(e) => changeRole(u.email, e.target.value as Role)}
                      style={{ ...inputStyle, height: "28px", minWidth: "140px" }}
                    >
                      {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
                    </select>
                  ) : (
                    <Chip color={roleColor[u.role]}>{ROLE_LABEL[u.role]}</Chip>
                  )}
                </td>
                <td style={{ color: UNK }}>
                  {u.scoped
                    ? <span style={{ color: "#cfd3e5" }}>
                        {u.effectiveRvaCount ?? 0} RVA{scopeExtra(u.scope)}
                        {u.dynamicScope && <span style={{ color: "#75798c", fontSize: "10px", marginLeft: "6px" }}>(team · auto)</span>}
                      </span>
                    : <span>— sees everything</span>}
                </td>
                <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                  {u.role !== "admin" && (
                    <button onClick={() => setEditing(u)} className="btn btn-secondary" style={{ fontSize: "11px", height: "26px", marginRight: "6px" }}>
                      <i className="ph ph-sliders-horizontal" /> Sửa phạm vi
                    </button>
                  )}
                  {canEditRoles && !u.fromCode && u.email !== session?.email && (
                    <button onClick={() => removeUser(u.email)} title="Gỡ quyền đăng nhập" className="btn btn-secondary" style={{ fontSize: "11px", height: "26px", color: "#e79a9a" }}>
                      <i className="ph ph-user-minus" />
                    </button>
                  )}
                  {u.fromCode && <span style={{ fontSize: "10px", color: "#595d6c" }}>code</span>}
                </td>
              </tr>
            ))}
            {usersQ.loading && <tr><td colSpan={4} style={{ color: UNK, padding: "14px" }}>Loading…</td></tr>}
            {usersQ.error && <tr><td colSpan={4} style={{ color: "#e79a9a", padding: "14px" }}>Config DB không truy cập được — không tải được danh sách.</td></tr>}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: "11px", color: "#75798c", marginTop: "10px", lineHeight: 1.6 }}>
        Đăng nhập = allowlist ở code <code style={{ color: "#9397ab" }}>access.ts</code> (gắn nhãn <em>code</em>, không gỡ được tại đây)
        {" "}<strong style={{ color: "#9397ab" }}>+ người dùng thêm tại đây</strong> (lưu ở <code style={{ color: "#9397ab" }}>analytics_portal</code>). Vai trò & phạm vi cũng lưu ở đó.
        {" "}Sales manager chỉ thấy dữ liệu của các RVA trong phạm vi; Admin/Board thấy tất cả và có thể “Xem với tư cách”.
      </div>

      {editing && (
        <ScopeEditor
          row={editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); usersQ.reload(); }}
        />
      )}
    </div>
  );
}

function scopeExtra(s?: SmScope | null): string {
  if (!s) return "";
  const bits: string[] = [];
  if (s.flowIds?.length) bits.push(`${s.flowIds.length} luồng`);
  if (s.jobIds?.length) bits.push(`${s.jobIds.length} lượt`);
  if (s.groupIds?.length) bits.push(`${s.groupIds.length} nhóm`);
  return bits.length ? ` · ${bits.join(" · ")}` : "";
}

function ScopeEditor({ row, onClose, onSaved }: { row: AdminUserRow; onClose: () => void; onSaved: () => void }) {
  const opts = useAsync(() => api.leadDistFilters(), []); // admin/board → unscoped universe
  const [rvas, setRvas] = useState<string[]>([]);
  const [flows, setFlows] = useState<string[]>([]);
  const [jobs, setJobs] = useState<string[]>([]);
  const [groups, setGroups] = useState<string[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.getScope(row.email).then(({ scope }) => {
      setRvas((scope.rvas ?? []).map(String));
      setFlows((scope.flowIds ?? []).map(String));
      setJobs((scope.jobIds ?? []).map(String));
      setGroups((scope.groupIds ?? []).map(String));
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [row.email]);

  const fmt = (n: number) => n.toLocaleString("en-US");
  const rvaOpts: Opt[] = (opts.data?.rvas ?? []).map((r) => ({ value: r.user, label: `${r.name} (${fmt(r.count)})` }));
  const flowOpts: Opt[] = (opts.data?.flows ?? []).map((f) => ({ value: String(f.id), label: `${f.name} (${fmt(f.count)})` }));
  const jobOpts: Opt[] = (opts.data?.jobs ?? []).map((j) => ({ value: j.id, label: `${j.name} (${fmt(j.count)})` }));
  const groupOpts: Opt[] = (opts.data?.groups ?? []).map((g) => ({ value: String(g.id), label: `${g.name} (${fmt(g.count)})` }));

  const save = async () => {
    setSaving(true);
    const scope: SmScope = {
      rvas, flowIds: flows.map(Number), jobIds: jobs, groupIds: groups.map(Number),
    };
    try { await api.setScope(row.email, scope); onSaved(); }
    catch { alert("Không lưu được phạm vi."); }
    finally { setSaving(false); }
  };

  return (
    <div onMouseDown={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 80, display: "grid", placeItems: "center", padding: "20px" }}>
      <div onMouseDown={(e) => e.stopPropagation()} style={{ ...cardPad, width: "min(560px, 96vw)", maxHeight: "86vh", overflowY: "auto", background: "#1c1e2b" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "10px" }}>
          <div>
            <div style={{ fontSize: "14px", color: "#e9e9ed" }}>Phạm vi của {row.name}</div>
            <div style={{ fontSize: "11px", color: "#75798c" }}>{row.email} · {ROLE_LABEL[row.role]}</div>
          </div>
          <button onClick={onClose} className="btn btn-secondary" style={{ height: "28px", fontSize: "12px" }}><i className="ph ph-x" /></button>
        </div>

        <div style={{ ...cardPad, background: "rgba(150,138,224,.10)", borderColor: "#423a6a", marginBottom: "10px", fontSize: "12px", color: "#cfd3e5" }}>
          <i className="ph ph-funnel" style={{ color: "#b5abfc", marginRight: "6px" }} />
          Phạm vi hiện tại:{" "}
          {row.scoped
            ? <strong style={{ color: "#d2cefd" }}>
                {row.effectiveRvaCount ?? 0} RVA{scopeExtra(row.scope)}
                {row.dynamicScope ? " (auto theo team)" : " (đã ghim)"}
              </strong>
            : <strong style={{ color: "#d2cefd" }}>Thấy toàn bộ (chưa giới hạn)</strong>}
        </div>

        <div style={{ fontSize: "11px", color: "#9397ab", marginBottom: "12px", lineHeight: 1.5 }}>
          {row.role === "sales_manager" ? (
            <>
              Sales manager <strong style={{ color: "#cfd3e5" }}>luôn giới hạn trong team</strong> — "Sửa phạm vi" chỉ <strong style={{ color: "#cfd3e5" }}>thu hẹp thêm</strong> bên trong team, không mở rộng ra ngoài.
              {" "}Để trống RVA = toàn bộ team (tự động, cập nhật động). Chọn RVA = chỉ các RVA đó <em>trong team</em> (RVA ngoài team bị bỏ qua).
              {" "}Luồng / lượt / nhóm = giới hạn thêm (AND).
            </>
          ) : (
            <>
              <strong style={{ color: "#cfd3e5" }}>Để trống tất cả</strong> = người dùng thấy <strong style={{ color: "#cfd3e5" }}>toàn bộ</strong> dữ liệu (không giới hạn).
              {" "}Chọn RVA / luồng / lượt / nhóm để <strong style={{ color: "#cfd3e5" }}>giới hạn</strong> phạm vi dữ liệu của họ.
            </>
          )}
        </div>

        {!loaded || opts.loading ? (
          <div style={{ color: UNK, padding: "20px 0" }}>Đang tải lựa chọn…</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <MultiSelect label={row.role === "sales_manager" ? "RVA (để trống = theo team)" : "RVA"} values={rvas} onChange={setRvas} options={rvaOpts} placeholder={row.role === "sales_manager" ? "Theo team (auto)" : "Không giới hạn"} />
            <MultiSelect label="Luồng phân phối (tuỳ chọn)" values={flows} onChange={setFlows} options={flowOpts} placeholder="Không giới hạn" />
            <MultiSelect label="Lượt phân phối (tuỳ chọn)" values={jobs} onChange={setJobs} options={jobOpts} placeholder="Không giới hạn" />
            <MultiSelect label="Nhóm tiếp nhận (tuỳ chọn)" values={groups} onChange={setGroups} options={groupOpts} placeholder="Không giới hạn" />
          </div>
        )}

        <div style={{ display: "flex", justifyContent: "flex-end", gap: "8px", marginTop: "16px" }}>
          <button onClick={onClose} className="btn btn-secondary" style={{ height: "32px", fontSize: "12px" }}>Huỷ</button>
          <button onClick={save} disabled={saving || !loaded} className="btn" style={{ height: "32px", fontSize: "12px", background: ACC, color: "#161826", border: 0, borderRadius: "8px", padding: "0 14px", cursor: "pointer" }}>
            {saving ? "Đang lưu…" : `Lưu (${rvas.length} RVA)`}
          </button>
        </div>
      </div>
    </div>
  );
}

